// ========================================
// Predictive Search (Production Ready)
// Shopify Theme Implementation
// ========================================

// ----------------------------------------
// Config
// ----------------------------------------
const API_URL = "https://quotes-api.usa-rs.com/shopify/search";

const CONFIG = {
  debounceDelay: 200,
  minQueryLength: 2,
  cacheTTL: 1000 * 60 * 5, // 5 minutes
  maxCacheEntries: 100,
};

const SCREEN_LARGE_MIN_WIDTH = 1024;
const LARGE_SCREEN_VENDOR_MAX = 4;
const LARGE_SCREEN_CATEGORY_MAX = 6;
const LARGE_SCREEN_PRODUCT_MAX = 8;
const SMALL_SCREEN_VENDOR_MAX = 2;
const SMALL_SCREEN_CATEGORY_MAX = 6;
const SMALL_SCREEN_PRODUCT_MAX = 6;
const MOBILE_POPUP_OPEN_CLASS = "search-suggest-mobile-open";
const DEFAULT_LOADING_TEXT = "Start typing for search results";
const ACTIVE_LOADING_TEXT = "Searching...";

// ----------------------------------------
// DOM
// ----------------------------------------
const input = document.querySelector("[data-search-suggest-input]");
const resultsContainer = document.querySelector("[data-search-suggest-results]");
const searchFormWrapper = document.querySelector(".search-suggest-form-wrapper");
const mainContent = document.querySelector("#MainContent");
const PREDICTIVE_SEARCH_INSTANCE_KEY = "__usaRsPredictiveSearchInstance";

// Exit early if feature doesn't exist
if (!input || !resultsContainer) {
  console.warn("Predictive search elements not found");
} else if (window[PREDICTIVE_SEARCH_INSTANCE_KEY]) {
  // Prevent duplicate listeners when this asset is executed multiple times
} else {
  window[PREDICTIVE_SEARCH_INSTANCE_KEY] = true;

  let controller = null;
  let initialized = false;

  /**
   * Cache Structure:
   * Map<query, { data, timestamp }>
   */
  const cache = new Map();

  const vendorsResults = resultsContainer.querySelector("[data-predictive-vendors-results]");
  const collectionsResults = resultsContainer.querySelector("[data-predictive-collections-results]");
  const productsResults = resultsContainer.querySelector("[data-predictive-products-results]");
  const productsLoading = resultsContainer.querySelector("[data-predictive-products-loading]");
  const productsNoResults = resultsContainer.querySelector("[data-predictive-products-no-results]");

  const vendorsSection = resultsContainer.querySelector(".search-suggest-vendors");
  const collectionsSection = resultsContainer.querySelector(".search-suggest-collections");
  const leftColumn = resultsContainer.querySelector(".search-suggest-left");

  const collectionTemplate = document.querySelector("#predictive-collection-result-template");
  const productTemplate = document.querySelector("#predictive-product-result-template");
  const closeButton = document.querySelector("[data-search-suggest-close]");

  // ----------------------------------------
  // Utilities
  // ----------------------------------------

  function debounce(fn, delay) {
    let timeout;

    return (...args) => {
      clearTimeout(timeout);

      timeout = setTimeout(() => {
        fn(...args);
      }, delay);
    };
  }

  function normalizeQuery(query) {
    return query.trim().toLowerCase();
  }

  function escapeHtml(value) {
    return String(value ?? "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#39;");
  }

  function getHighlightedTitle(title, query) {
    const text = String(title ?? "");
    const searchQuery = String(query ?? "").trim();

    if (!searchQuery) return escapeHtml(text);

    const lowerText = text.toLowerCase();
    const lowerQuery = searchQuery.toLowerCase();
    const matchStartIndex = lowerText.indexOf(lowerQuery);

    if (matchStartIndex === -1) {
      return escapeHtml(text);
    }

    const matchEndIndex = matchStartIndex + searchQuery.length;
    const beforeMatch = text.slice(0, matchStartIndex);
    const matchedText = text.slice(matchStartIndex, matchEndIndex);
    const afterMatch = text.slice(matchEndIndex);

    const beforeMarkup = beforeMatch
      ? `<span class="font-bold color-secondary">${escapeHtml(beforeMatch)}</span>`
      : "";

    const matchMarkup = `<span class="font-normal">${escapeHtml(matchedText)}</span>`;

    const afterMarkup = afterMatch
      ? `<span class="font-bold color-secondary">${escapeHtml(afterMatch)}</span>`
      : "";

    return `${beforeMarkup}${matchMarkup}${afterMarkup}`;
  }

  function setFirstImageSrc(markup, imageUrl) {
    if (!imageUrl) {
      return markup;
    }

    return markup.replace(
      /(<img\b[^>]*\bsrc=)(["']).*?\2/i,
      `$1"${escapeHtml(imageUrl)}"`
    );
  }

  function fillTemplate(template, replacements) {
    let markup = template;

    Object.entries(replacements).forEach(([key, value]) => {
      if (value === undefined || value === null) {
        return;
      }

      markup = markup.split(`[${key}]`).join(value);
    });

    return markup;
  }

  function renderEntityList(items, templateElement, mapper) {
    if (!templateElement || !Array.isArray(items) || items.length === 0) {
      return "";
    }

    const template = templateElement.innerHTML.trim();

    return items
      .map((item) => {
        const replacements = mapper(item);
        const filledMarkup = fillTemplate(template, replacements);

        return setFirstImageSrc(filledMarkup, replacements.image);
      })
      .join("");
  }

  function getLoadingCounts() {
    const isLargeScreen = window.matchMedia(
      `(min-width: ${SCREEN_LARGE_MIN_WIDTH}px)`
    ).matches;

    return {
      vendors: isLargeScreen ? LARGE_SCREEN_VENDOR_MAX : SMALL_SCREEN_VENDOR_MAX,
      categories: isLargeScreen ? LARGE_SCREEN_CATEGORY_MAX : SMALL_SCREEN_CATEGORY_MAX,
      products: isLargeScreen ? LARGE_SCREEN_PRODUCT_MAX : SMALL_SCREEN_PRODUCT_MAX,
    };
  }

  function createLoadingItems(count, includeImage = false) {
    return Array.from({ length: Math.max(1, count) })
      .map(() => `
        <li class="search-suggest__result-item list-none search-suggest__result-item--loading" aria-hidden="true">
          <div class="flex items-center gap-2">
            ${includeImage ? '<span class="search-suggest__skeleton search-suggest__skeleton-image hidden md:block"></span>' : ""}
            <div class="flex flex-col gap-1 w-full">
              <span class="search-suggest__skeleton search-suggest__skeleton-line"></span>
              <span class="search-suggest__skeleton search-suggest__skeleton-line search-suggest__skeleton-line--short"></span>
            </div>
          </div>
        </li>
      `)
      .join("");
  }

  function formatPrice(price) {
    const amount = Number(price);

    if (Number.isNaN(amount)) {
      return "";
    }

    return `$${amount.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }

  // ----------------------------------------
  // Cache
  // ----------------------------------------

  function getCached(query) {
    const entry = cache.get(query);

    if (!entry) return null;

    const isExpired =
      Date.now() - entry.timestamp > CONFIG.cacheTTL;

    if (isExpired) {
      cache.delete(query);
      return null;
    }

    return entry.data;
  }

  function setCache(query, data) {
    // Prevent unbounded memory growth
    if (cache.size >= CONFIG.maxCacheEntries) {
      const firstKey = cache.keys().next().value;
      cache.delete(firstKey);
    }

    cache.set(query, {
      data,
      timestamp: Date.now(),
    });
  }

  // ----------------------------------------
  // UI
  // ----------------------------------------

  function clearResults() {
    if (vendorsResults) vendorsResults.innerHTML = "";
    if (collectionsResults) collectionsResults.innerHTML = "";
    if (productsResults) {
      productsResults.innerHTML = `
        <li class="search-suggest__result-item list-none color-secondary text-sm" data-predictive-products-loading>
          ${DEFAULT_LOADING_TEXT}
        </li>
      `;
    }

    if (productsLoading) productsLoading.classList.remove("hidden");
    if (productsNoResults) productsNoResults.classList.add("hidden");

    if (vendorsSection) vendorsSection.hidden = false;
    if (collectionsSection) collectionsSection.hidden = false;
    if (leftColumn) leftColumn.hidden = false;

    resultsContainer.classList.remove("no-vendors-categories");
    resultsContainer.hidden = true;
    setDesktopSearchActive(false);
  }

  function setDesktopSearchActive(isActive) {
    // if (isSmallScreen()) {
    //   if (mainContent) {
    //     mainContent.classList.remove("search-suggest-open");
    //   }

    //   return;
    // }

    if (mainContent) {
      mainContent.classList.toggle("search-suggest-open", isActive);
    }

    if (resultsContainer) {
      resultsContainer.style.display = isActive ? "block" : "none";
    }
    document.body.style.overflow = "";
  }

  function isSmallScreen() {
    return !window.matchMedia(`(min-width: ${SCREEN_LARGE_MIN_WIDTH}px)`).matches;
  }

  function openMobileSearchPopup() {
    if (!isSmallScreen() || !searchFormWrapper) {
      return;
    }

    searchFormWrapper.classList.add(MOBILE_POPUP_OPEN_CLASS);
    document.body.style.overflow = "hidden";

    if (resultsContainer.hidden) {
      showLoading();
    }
  }

  function closeMobileSearchPopup() {
    if (!searchFormWrapper) {
      return;
    }

    searchFormWrapper.classList.remove(MOBILE_POPUP_OPEN_CLASS);
    document.body.style.overflow = "";
    clearResults();
    input.blur();
  }

  function showLoading() {
    resultsContainer.hidden = false;

    const loadingCounts = getLoadingCounts();

    if (vendorsResults) {
      vendorsResults.innerHTML = createLoadingItems(
        Math.min(loadingCounts.vendors, 3),
        false
      );
    }

    if (collectionsResults) {
      collectionsResults.innerHTML = createLoadingItems(
        Math.min(loadingCounts.categories, 3),
        false
      );
    }

    if (productsResults) {
      productsResults.innerHTML = createLoadingItems(
        Math.min(loadingCounts.products, 4),
        true
      );
    }

    if (productsNoResults) productsNoResults.classList.add("hidden");
    if (productsLoading) {
      productsLoading.textContent = ACTIVE_LOADING_TEXT;
      productsLoading.classList.remove("hidden");
    }

    if (vendorsSection) vendorsSection.hidden = false;
    if (collectionsSection) collectionsSection.hidden = false;
    if (leftColumn) leftColumn.hidden = false;

    resultsContainer.classList.remove("no-vendors-categories");
    setDesktopSearchActive(true);
  }

  function renderSuggestions(data, query) {
    const allProducts = Array.isArray(data?.products) ? data.products : [];
    const allVendors = Array.isArray(data?.vendors) ? data.vendors : [];
    const allCategories = Array.isArray(data?.categories) ? data.categories : [];

    const isLargeScreen = window.matchMedia(
      `(min-width: ${SCREEN_LARGE_MIN_WIDTH}px)`
    ).matches;

    const vendorsLimit = isLargeScreen
      ? LARGE_SCREEN_VENDOR_MAX
      : SMALL_SCREEN_VENDOR_MAX;
    const categoriesLimit = isLargeScreen
      ? LARGE_SCREEN_CATEGORY_MAX
      : SMALL_SCREEN_CATEGORY_MAX;
    const productsLimit = isLargeScreen
      ? LARGE_SCREEN_PRODUCT_MAX
      : SMALL_SCREEN_PRODUCT_MAX;

    const vendors = allVendors.slice(0, vendorsLimit);
    const categories = allCategories.slice(0, categoriesLimit);
    const products = allProducts.slice(0, productsLimit);

    const vendorsHtml = renderEntityList(vendors, collectionTemplate, (item) => ({
      url: escapeHtml(item?.url),
      alt_title: escapeHtml(item?.title),
      title: getHighlightedTitle(item?.title, query),
    }));

    const categoriesHtml = renderEntityList(categories, collectionTemplate, (item) => ({
      url: escapeHtml(item?.url),
      alt_title: escapeHtml(item?.title),
      title: getHighlightedTitle(item?.title, query),
    }));

    const productsHtml = renderEntityList(products, productTemplate, (item) => ({
      url: escapeHtml(item?.url),
      image: item?.thumbnail ? item.thumbnail : undefined,
      alt_title: escapeHtml(item?.title),
      title: `${item.vendor.title} Model No. ${item.model}`,
      price: escapeHtml(formatPrice(item?.price)),
    }));

    if (vendorsResults) vendorsResults.innerHTML = vendorsHtml;
    if (collectionsResults) collectionsResults.innerHTML = categoriesHtml;
    if (productsResults) productsResults.innerHTML = productsHtml;

    const hasVendors = vendors.length > 0;
    const hasCategories = categories.length > 0;
    const hasProducts = products.length > 0;
    const hasVendorsOrCategories = hasVendors || hasCategories;

    if (vendorsSection) vendorsSection.hidden = !hasVendors;
    if (collectionsSection) collectionsSection.hidden = !hasCategories;
    if (leftColumn) leftColumn.hidden = !hasVendorsOrCategories;

    resultsContainer.classList.toggle(
      "no-vendors-categories",
      !hasVendorsOrCategories
    );

    if (productsLoading) productsLoading.classList.add("hidden");
    if (productsNoResults) {
      productsNoResults.classList.toggle("hidden", hasProducts);
    }

    resultsContainer.hidden = false;
  }

  // ----------------------------------------
  // API
  // ----------------------------------------

  async function fetchSuggestions(query) {

    // 1. Cache First
    const cached = getCached(query);

    if (cached) {
      return cached;
    }

    // 2. Abort Previous Request
    if (controller) {
      controller.abort();
    }

    controller = new AbortController();

    const url = new URL(API_URL);

    url.searchParams.set("q", query);

    const response = await fetch(url.toString(), {
      method: "GET",
      signal: controller.signal,
      headers: {
        "Accept": "application/json",
        "Access-Control-Allow-Origin": "*",
      },
    });

    if (!response.ok) {
      throw new Error(
        `Predictive search failed: ${response.status}`
      );
    }

    const data = await response.json();

    // 3. Cache Result
    setCache(query, data);
    console.log(JSON.stringify(data));

    return data;
  }

  // ----------------------------------------
  // Search Handler
  // ----------------------------------------

  const handleSearch = debounce(async (query) => {
    try {
      showLoading();

      const results = await fetchSuggestions(query);
      //const results = JSON.parse(sampleJsonTest());
      console.log(results);

      renderSuggestions(results, query);

    } catch (error) {

      // Ignore aborted requests
      if (error.name === "AbortError") {
        return;
      }

      console.error(error);

      clearResults();
    }

  }, CONFIG.debounceDelay);

  // ----------------------------------------
  // Event Handlers
  // ----------------------------------------

  function onInput(event) {
    const rawQuery = event.target.value;
    const query = normalizeQuery(rawQuery);

    // Minimum character requirement
    if (query.length < CONFIG.minQueryLength) {
      clearResults();
      return;
    }

    const hasTypedValue = rawQuery.trim().length > 0;
    setDesktopSearchActive(hasTypedValue);
    openMobileSearchPopup();
    handleSearch(query);
  }

  function onInputFocus() {
    if (input.value.trim().length > 0) {
      setDesktopSearchActive(true);
    }

    openMobileSearchPopup();
  }

  function onDocumentClick(event) {
    if (isSmallScreen() && searchFormWrapper?.classList.contains(MOBILE_POPUP_OPEN_CLASS)) {
      return;
    }

    const isInside =
      input.contains(event.target) ||
      resultsContainer.contains(event.target);

    if (!isInside) {
      clearResults();
    }
  }

  function onDocumentKeydown(event) {
    if (event.key !== "Escape") {
      return;
    }

    if (!isSmallScreen()) {
      clearResults();
      return;
    }

    if (!isSmallScreen() || !searchFormWrapper?.classList.contains(MOBILE_POPUP_OPEN_CLASS)) {
      return;
    }

    closeMobileSearchPopup();
  }

  // ----------------------------------------
  // Lazy Initialization
  // ----------------------------------------

  function initializePredictiveSearch() {

    if (initialized) return;

    initialized = true;

    input.addEventListener("input", onInput);
    input.addEventListener("focus", onInputFocus);

    onInputFocus();

    document.addEventListener("click", onDocumentClick);
    document.addEventListener("keydown", onDocumentKeydown);

    if (closeButton) {
      closeButton.addEventListener("click", closeMobileSearchPopup);
    }
  }

  // Initialize only when user interacts
  input.addEventListener(
    "focus",
    initializePredictiveSearch,
    { once: true }
  );
}
